import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/Footer.css';

// Import assets
import facebookIcon from '../assets/fb.png';
import twitterIcon from '../assets/twitter.png';
import instagramIcon from '../assets/instag.png';
import linkedinIcon from '../assets/link.png';
import youtubeIcon from '../assets/youtube.png';
import locationIcon from '../assets/map.png';
import phoneIcon from '../assets/call.png';
import emailIcon from '../assets/gmail.png';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', url: '/', isPage: true },
    { name: 'Services', url: '#services', isPage: false },
    { name: 'About Us', url: '#about', isPage: false },
    { name: 'Contact', url: '#contact', isPage: false },
    { name: 'Testimonials', url: '#testimonials', isPage: false }
  ];

  const services = [
    { name: 'General Pest Control', url: '#services', isPage: false },
    { name: 'Termite Treatment', url: '#services', isPage: false },
    { name: 'Bed Bug Extermination', url: '#services', isPage: false },
    { name: 'Rodent Removal', url: '#services', isPage: false },
    { name: 'Mosquito Control', url: '#services', isPage: false },
    { name: 'Commercial Services', url: '#services', isPage: false }
  ];

  const socialLinks = [
    { name: 'Facebook', icon: facebookIcon, url: 'https://facebook.com', alt: 'Facebook' },
    { name: 'Twitter', icon: twitterIcon, url: 'https://twitter.com', alt: 'Twitter' },
    { name: 'Instagram', icon: instagramIcon, url: 'https://instagram.com', alt: 'Instagram' },
    { name: 'LinkedIn', icon: linkedinIcon, url: 'https://linkedin.com', alt: 'LinkedIn' },
    { name: 'YouTube', icon: youtubeIcon, url: 'https://youtube.com', alt: 'YouTube' }
  ];

  // Smooth scroll function for hash links
  const handleHashScroll = (e, targetId) => {
    e.preventDefault();
    
    // Check if we're on the home page
    if (window.location.pathname !== '/') {
      // If not on home page, navigate to home first
      window.location.href = `/${targetId}`;
      return;
    }
    
    const targetElement = document.getElementById(targetId.replace('#', ''));
    if (targetElement) {
      const navbarHeight = document.querySelector('.navbar')?.offsetHeight || 0;
      const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset - navbarHeight;
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="footer-section">
      <div className="footer-container">
        {/* Main Footer Content */}
        <div className="footer-grid">
          {/* Company Info */}
          <div className="footer-company">
            <div className="footer-logo">
              <Link to="/" className="footer-logo-link">
                <span className="footer-logo-icon">🐜</span>
                <span className="footer-logo-text">PestControl <span className="footer-logo-highlight">Today</span></span>
              </Link>
            </div>
            <p className="footer-company-description">
              Professional pest control services across the United States. 
              We're committed to keeping your home and business pest-free 
              with safe, effective, and eco-friendly solutions.
            </p>
            <div className="footer-social">
              {socialLinks.map((social) => (
                <a 
                  key={social.name}
                  href={social.url} 
                  className="footer-social-link"
                  aria-label={social.name}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img 
                    src={social.icon} 
                    alt={social.alt} 
                    className="footer-social-icon"
                  />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-links">
            <h4 className="footer-heading">Quick Links</h4>
            <ul className="footer-link-list">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {link.isPage ? (
                    <Link to={link.url} className="footer-link">{link.name}</Link>
                  ) : (
                    <a 
                      href={link.url} 
                      className="footer-link"
                      onClick={(e) => handleHashScroll(e, link.url)}
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="footer-links">
            <h4 className="footer-heading">Our Services</h4>
            <ul className="footer-link-list">
              {services.map((service) => (
                <li key={service.name}>
                  <a 
                    href={service.url} 
                    className="footer-link"
                    onClick={(e) => handleHashScroll(e, service.url)}
                  >
                    {service.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div className="footer-contact">
            <h4 className="footer-heading">Contact Info</h4>
            <div className="footer-contact-item">
              <img src={locationIcon} alt="Location" className="footer-contact-icon-img" />
              <span className="footer-contact-text">
                123 Pest Free Lane<br />
                Suite 200<br />
                New York, NY 10001
              </span>
            </div>
            <div className="footer-contact-item">
              <img src={phoneIcon} alt="Phone" className="footer-contact-icon-img" />
              <span className="footer-contact-text">
                <a href="tel:+18334936242" className="footer-contact-link">+1 (833) 493-6242</a>
              </span>
            </div>
            <div className="footer-contact-item">
              <img src={emailIcon} alt="Email" className="footer-contact-icon-img" />
              <span className="footer-contact-text">
                <a href="mailto:info@pestcontroltoday.com" className="footer-contact-link">
                  info@pestcontroltoday.com
                </a><br />
                <a href="mailto:support@pestcontroltoday.com" className="footer-contact-link">
                  support@pestcontroltoday.com
                </a>
              </span>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="footer-copyright">
              &copy; {currentYear} PestControl Today. All rights reserved.
            </p>
            <div className="footer-bottom-links">
              <Link to="/privacy-policy" className="footer-bottom-link">Privacy Policy</Link>
              <span className="footer-bottom-divider">|</span>
              <Link to="/terms-of-service" className="footer-bottom-link">Terms of Service</Link>
              <span className="footer-bottom-divider">|</span>
              <a href="#" className="footer-bottom-link">Sitemap</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;